/**
 * MarkupShapes — draws stored shapes into an SVG whose viewBox is the photo's own pixel box
 * (w x h). Used by the read-only overlay and by the editor, so a drawing looks the same in both.
 */
import type { MarkupShape } from '../../../../types/fieldService/generatorPhotoMarkupVocab';
import { arrowHeadPath, fontPx, MARKUP_COLOR_HEX, strokePx } from './markupGeometry';

interface MarkupShapesProps {
  shapes: MarkupShape[];
  w: number;
  h: number;
  selectedId?: string | null;
  /** Editor only: pointer-down on a shape (select tool). */
  onShapePointerDown?: (id: string, e: React.PointerEvent<SVGElement>) => void;
}

/** A dark halo behind every mark, so red on a red generator or white on a white wall still reads. */
const HALO = 'rgba(0,0,0,0.55)';

function ShapeBody({ shape, w, h }: { shape: MarkupShape; w: number; h: number }) {
  const color = MARKUP_COLOR_HEX[shape.color];
  const sw = strokePx(shape.width, w, h);
  const line = { stroke: color, strokeWidth: sw, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const halo = { ...line, stroke: HALO, strokeWidth: sw + Math.max(2, sw * 0.8) };

  switch (shape.type) {
    case 'arrow': {
      const d = `M ${shape.from.x * w} ${shape.from.y * h} L ${shape.to.x * w} ${shape.to.y * h}`;
      const head = arrowHeadPath(shape.from, shape.to, w, h, sw);
      return (
        <>
          <path d={d} {...halo} />
          <path d={head} {...halo} />
          <path d={d} {...line} />
          <path d={head} {...line} />
        </>
      );
    }
    case 'ellipse':
      return (
        <>
          <ellipse cx={shape.cx * w} cy={shape.cy * h} rx={shape.rx * w} ry={shape.ry * h} {...halo} />
          <ellipse cx={shape.cx * w} cy={shape.cy * h} rx={shape.rx * w} ry={shape.ry * h} {...line} />
        </>
      );
    case 'freehand': {
      const points = shape.points.map((p) => `${p.x * w},${p.y * h}`).join(' ');
      return (
        <>
          <polyline points={points} {...halo} />
          <polyline points={points} {...line} />
        </>
      );
    }
    case 'text': {
      const size = fontPx(shape.size, w, h);
      return (
        <text
          x={shape.at.x * w}
          y={shape.at.y * h}
          fontSize={size}
          fontWeight={700}
          fill={color}
          stroke={HALO}
          strokeWidth={Math.max(2, size * 0.12)}
          paintOrder="stroke"
          dominantBaseline="hanging"
          style={{ fontFamily: 'inherit' }}
        >
          {shape.text}
        </text>
      );
    }
    case 'textBox': {
      const size = fontPx(shape.size, w, h);
      const pad = size * 0.35;
      return (
        <>
          <rect x={shape.x * w} y={shape.y * h} width={shape.w * w} height={shape.h * h} rx={size * 0.3} fill="rgba(15,23,42,0.72)" stroke={color} strokeWidth={sw} />
          <foreignObject x={shape.x * w + pad} y={shape.y * h + pad} width={Math.max(1, shape.w * w - pad * 2)} height={Math.max(1, shape.h * h - pad * 2)}>
            <div style={{ color, fontSize: size, fontWeight: 700, lineHeight: 1.15, overflowWrap: 'anywhere', overflow: 'hidden', height: '100%' }}>
              {shape.text}
            </div>
          </foreignObject>
        </>
      );
    }
  }
}

export function MarkupShapes({ shapes, w, h, selectedId, onShapePointerDown }: MarkupShapesProps) {
  return (
    <>
      {shapes.map((shape) => (
        <g
          key={shape.id}
          data-shape-id={shape.id}
          data-shape-type={shape.type}
          onPointerDown={onShapePointerDown ? (e) => onShapePointerDown(shape.id, e) : undefined}
          style={onShapePointerDown ? { cursor: 'move' } : undefined}
          opacity={selectedId && selectedId !== shape.id ? 0.55 : 1}
        >
          <ShapeBody shape={shape} w={w} h={h} />
        </g>
      ))}
    </>
  );
}
